import {
  ChangeEvent,
  ComponentType,
  HTMLAttributes,
  ReactNode,
  KeyboardEvent,
} from "react"

import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions,
} from "@material-ui/lab/Autocomplete"
import {
  Checkbox,
  Popper,
  PopperProps,
  TextField,
  Tooltip,
} from "@material-ui/core"
import { css, cx } from "@emotion/css"

import { FilterOptionsState } from "@material-ui/lab/useAutocomplete"

type Props = {
  items: Array<any>
  selectedValues: Array<any>
  renderOption: (option: Record<string, any>) => string | ReactNode
  selectAllOption?: Record<string, any>
  selectAllValue?: string
  noOptionsText?: string
  onToggleOption: (options: Array<any>) => void
  onClearOptions?: VoidFunction
  onSelectAll?: (isSelected: boolean) => void
  getOptionLabel: (option: Record<string, any>) => string
  onBlur?: (e: any) => void
  onFocus?: VoidFunction
  paperComponent?: ComponentType<HTMLAttributes<HTMLElement>>
  autofocus?: boolean
  label?: string
  placeholder?: string
  checkValues?: (option?: Record<string, any>) => boolean
  open?: boolean
  onOpen?: VoidFunction
  onClose?: VoidFunction
  groupBy?: (option: Record<string, any>) => string
  isGrouped?: boolean
  rootClassName?: string
  paperClassName?: string
  disabled?: boolean
  className?: string
  errorMessage?: string
  inputClassName?: string
  optionClassName?: string
  listClassName?: string
  inputValue?: string
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode
  hasMoreLabel?: string
  endAdornment?: ReactNode
  disabledCheckbox?: boolean
  size?: "medium" | "small"
  margin?: "none" | "dense" | "normal"
  onKeyDown?: (evt: KeyboardEvent<HTMLDivElement>) => void
  tooltipName?: string
}

export const MultiSelect = (props: Props) => {
  const {
    items,
    selectedValues,
    renderOption,
    selectAllOption,
    selectAllValue,
    noOptionsText,
    onToggleOption,
    onClearOptions,
    onSelectAll,
    getOptionLabel,
    onBlur,
    onFocus,
    paperComponent,
    autofocus = false,
    label,
    placeholder,
    checkValues,
    open,
    onOpen,
    onClose,
    groupBy,
    isGrouped = false,
    rootClassName,
    errorMessage,
    className,
    paperClassName,
    inputClassName,
    optionClassName,
    listClassName,
    inputValue,
    disabled = false,
    disabledCheckbox = false,
    size = "medium",
    margin = "none",
    renderInput,
    endAdornment,
    onKeyDown,
    tooltipName,
  } = props

  const allSelected =
    !!items.length && selectAllValue && items.length === selectedValues.length
  const handleToggleSelectAll = () => {
    onSelectAll && onSelectAll(!allSelected)
  }
  const handleChange = (
    _: ChangeEvent<{}>,
    selectedOptions: Record<string, any>[],
    reason: AutocompleteChangeReason,
  ) => {
    const _selectedFiltered = selectedOptions?.filter((o) => !o?.disabled)
    if (reason === "select-option" || reason === "remove-option") {
      if (
        selectedOptions.find(
          (option) => !!option.value && option.value === selectAllValue,
        )
      ) {
        !disabledCheckbox && handleToggleSelectAll()
      } else {
        !disabledCheckbox && onToggleOption && onToggleOption(_selectedFiltered)
      }
    } else if (reason === "clear") {
      !disabledCheckbox && onClearOptions && onClearOptions()
    }
  }

  const error = !!errorMessage

  const filter = createFilterOptions()
  return (
    <>
      <Autocomplete
        multiple
        options={items}
        value={selectedValues}
        disableCloseOnSelect
        open={open}
        onOpen={onOpen}
        openOnFocus={autofocus}
        groupBy={groupBy}
        getOptionLabel={getOptionLabel}
        disabled={disabled}
        getOptionSelected={(option, anotherOption) =>
          checkValues && checkValues(option)
            ? option?.value === anotherOption?.value
            : option?.id === anotherOption?.id
        }
        noOptionsText={noOptionsText}
        filterOptions={(options, params) => {
          const filtered = filter(
            options,
            params as FilterOptionsState<unknown>,
          )
          const selectAllLine = !!selectAllOption
            ? [{ name: selectAllOption?.name, value: selectAllOption?.value }]
            : null
          return [...(selectAllLine || []), ...filtered] as Record<
            string,
            string
          >[]
        }}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onClose={onClose}
        onKeyDown={onKeyDown}
        className={className}
        PopperComponent={PopperComponent}
        PaperComponent={paperComponent}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox
              edge="start"
              color="primary"
              disableRipple
              checked={allSelected || (!option?.disabled && selected)}
              classes={{ root: styles.checker }}
              disabled={option?.disabled || disabledCheckbox}
            />
            <Tooltip
              title={option?.name || ""}
              disableHoverListener={
                !tooltipName || !option?.name?.includes(tooltipName)
              }
            >
              <span className={cx("BodyBody-2BlackRegular", styles.wrap)}>
                {renderOption(option)}
              </span>
            </Tooltip>
          </>
        )}
        renderInput={(params) => {
          if (renderInput) return renderInput(params)
          return (
            <div ref={params.InputProps.ref}>
              <TextField
                variant="outlined"
                size={size}
                margin={margin}
                type="text"
                label={label}
                helperText={errorMessage}
                placeholder={placeholder}
                {...params.inputProps}
                error={!!error}
                autoFocus={autofocus}
                value={inputValue}
                InputLabelProps={{
                  shrink: !!inputValue || undefined,
                  classes: { root: styles.label },
                }}
                InputProps={{
                  endAdornment: endAdornment,
                  inputProps: {
                    autoComplete: "new-password",
                    form: {
                      autoComplete: "off",
                    },
                  },
                }}
              />
            </div>
          )
        }}
        classes={{
          root: cx(styles.fullwidth, rootClassName),
          option: cx(styles.option, optionClassName),
          listbox: cx(
            { [styles.listBox]: !!selectAllOption },
            { [styles.listBoxGrouped]: isGrouped },
            listClassName,
          ),
          paper: cx(styles.paper, paperClassName),
          input: inputClassName,
        }}
      />
    </>
  )
}

const PopperComponent = (props: PopperProps) => (
  <Popper {...props} style={{ width: 400 }} placement="bottom-start" />
)

const styles = {
  checker: css`
    .MuiSvgIcon-root {
      width: var(--spacing-xxl);
      height: var(--spacing-xxl);
    }

    padding: 2px 6px 2px 0;
  `,
  option: css`
    font-size: 13px;
    width: 100%;
    padding-top: 0;
    padding-bottom: 0;

    &[aria-selected="true"] {
      background-color: transparent;
      width: 100%;
    }

    &:hover[aria-selected="true"] {
      background-color: var(--primary-light);
      width: 100%;
    }

    &[data-focus="true"] {
      background-color: var(--primary-light);
      width: 100%;
    }
  `,
  listBox: css`
    > li {
      > div {
        line-height: unset;
        display: flex;
        align-items: center;
        text-transform: capitalize;
      }
    }

    > li:first-child {
      height: var(--spacing-xxl-4);
      margin-bottom: var(--spacing-xs);
      border-bottom: 1px solid var(--light-grey);

      > div {
        height: 0;
      }
    }

    padding: var(--spacing-xs);
  `,
  listBoxGrouped: css`
    > li {
      > div {
        font-size: 12px;
        color: var(--dark-grey);
      }
    }

    > li:first-child {
      > ul {
        > li:first-child {
          border-bottom: none;
          height: var(--spacing-xxl-4);
        }
      }
    }

    > li:not(:last-child) {
      margin-bottom: var(--spacing-l);
    }
  `,
  paper: css`
    width: 270px;
  `,
  fullwidth: css`
    width: 100%;
  `,
  placeholder: css`
    color: var(--black);
  `,
  wrap: css`
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  label: css`
    &.Mui-focused {
      color: var(--dark-grey);
    }
  `,
}
